import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Client } from "openapi-fetch";
import type { paths, components } from "../api/generated";
import { createPaginatedInfiniteQuery, type QueryApi, type SpringPagedResponse } from "../api/query";

type WishlistCountResponse = components["schemas"]["WishlistCountResponse"];
type WishlistCheckResponse = components["schemas"]["WishlistCheckResponse"];
type WishlistBatchCheckResponse = components["schemas"]["WishlistBatchCheckResponse"];
type ProductSearchItemDto = components["schemas"]["ProductSearchItemDto"];
type WishlistPage = SpringPagedResponse<ProductSearchItemDto>;
type FetchClient = Client<paths, "*/*">;

const STALE_TIME_MS = 30_000;

export const wishlistKeys = {
  count: () => ["get", "/wishlist/count", {}] as const,
  check: (productId: string) =>
    ["get", "/wishlist/check/{productId}", { params: { path: { productId } } }] as const,
  list: () => ["get", "/wishlist"] as const,
  batchCheck: () => ["post", "/wishlist/check-batch"] as const,
};

export function createUseWishlistCount($api: QueryApi) {
  return function useWishlistCount(options?: { enabled?: boolean }) {
    return $api.useQuery("get", "/wishlist/count", {}, {
      staleTime: STALE_TIME_MS,
      select: (data: WishlistCountResponse) => data?.count ?? 0,
      ...options,
    });
  };
}

export function createUseIsWishlisted($api: QueryApi) {
  return function useIsWishlisted(productId: string | undefined) {
    return $api.useQuery(
      "get",
      "/wishlist/check/{productId}",
      { params: { path: { productId: productId! } } },
      {
        enabled: !!productId,
        staleTime: STALE_TIME_MS,
        select: (data: WishlistCheckResponse) => data?.wishlisted ?? false,
      },
    );
  };
}

export function createUseWishlistProductsInfinite($api: QueryApi) {
  const usePaginatedInfiniteQuery = createPaginatedInfiniteQuery($api);

  return function useWishlistProductsInfinite(size = 20, options?: { enabled?: boolean }) {
    return usePaginatedInfiniteQuery<WishlistPage, ProductSearchItemDto>(
      "/wishlist",
      { size },
      options,
    );
  };
}

export function createUseWishlistBatchCheck($api: QueryApi) {
  return function useWishlistBatchCheck(productIds: string[]) {
    return $api.useQuery(
      "post",
      "/wishlist/check-batch",
      { body: { productIds } },
      {
        enabled: productIds.length > 0,
        staleTime: STALE_TIME_MS,
        select: (data: WishlistBatchCheckResponse) => data?.results ?? {},
      },
    );
  };
}

export function createUseToggleWishlist($fetch: FetchClient) {
  return function useToggleWishlist() {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async ({ productId, wishlisted }: { productId: string; wishlisted: boolean }) => {
        if (wishlisted) {
          await $fetch.DELETE("/wishlist/{productId}", {
            params: { path: { productId } },
          });
        } else {
          await $fetch.POST("/wishlist/{productId}", {
            params: { path: { productId } },
          });
        }
      },
      onMutate: async ({ productId, wishlisted }) => {
        const checkKey = wishlistKeys.check(productId);
        const countKey = wishlistKeys.count();

        await queryClient.cancelQueries({ queryKey: checkKey });
        await queryClient.cancelQueries({ queryKey: countKey });

        const previousCheck = queryClient.getQueryData(checkKey);
        const previousCount = queryClient.getQueryData(countKey);

        queryClient.setQueryData(checkKey, { wishlisted: !wishlisted });
        queryClient.setQueryData(countKey, (old: WishlistCountResponse | undefined) => {
          const current = old?.count ?? 0;
          return { count: wishlisted ? Math.max(0, current - 1) : current + 1 };
        });

        return { previousCheck, previousCount, checkKey, countKey };
      },
      onError: (_err, _vars, context) => {
        if (context) {
          queryClient.setQueryData(context.checkKey, context.previousCheck);
          queryClient.setQueryData(context.countKey, context.previousCount);
        }
      },
      onSettled: (_data, _error, variables) => {
        queryClient.invalidateQueries({ queryKey: wishlistKeys.list() });
        queryClient.invalidateQueries({ queryKey: wishlistKeys.check(variables.productId) });
        queryClient.invalidateQueries({ queryKey: wishlistKeys.count() });
        queryClient.invalidateQueries({ queryKey: wishlistKeys.batchCheck() });
      },
    });
  };
}
